import { Option, Select } from "@components/Select";
import { TextArea } from "@components/TextInput";
import { DonateCustomField } from "@components/donate/DonateV3/PaymentProcess/useDonateCustomFields";
import { DonateFormType } from "@components/donate/DonateV3/types";
import styled from "@emotion/styled";
import React from "react";
import { UseFormReturn } from "react-hook-form";

import { CustomFieldType } from "@every.org/common/src/codecs/donationFlowCustomization";

import { colorCssVars } from "src/theme/color";
import { verticalStackCss } from "src/theme/spacing";
import { FontWeight, TextSize, textSizeCss } from "src/theme/text";

const FieldContainer = styled.div`
  ${verticalStackCss.xs};
`;

const Label = styled.label`
  color: var(${colorCssVars.text.body});
  font-weight: ${FontWeight.BOLD};
  ${textSizeCss[TextSize.s]};
`;

export const CustomFieldsInput = ({
  form,
  customFields,
}: {
  form: UseFormReturn<DonateFormType>;
  customFields?: DonateCustomField[];
}) => {
  if (!customFields?.length) {
    return null;
  }

  const responses = form.watch("customFieldResponses") || {};
  const customFieldsErrors =
    (form.formState.errors.customFieldResponses as
      | Record<string, { message?: string }>
      | undefined) || {};

  const upsertResponse = (fieldKey: string, value: string) => {
    const currentResponses = form.getValues("customFieldResponses") || {};
    const nextResponses = {
      ...currentResponses,
      [fieldKey]: value,
    };
    form.setValue("customFieldResponses", nextResponses);
    if (form.formState.errors.customFieldResponses) {
      form.clearErrors("customFieldResponses");
    }
  };

  return (
    <div css={verticalStackCss.m}>
      {customFields.map((field, index) => {
        const fallbackKey = `customField_${index}`;
        const fieldKey = field.formKey || fallbackKey;
        const inputId = `customField-${fieldKey}`;
        const fieldValue =
          responses[fieldKey] ?? responses[field.heading] ?? "";
        const fieldError = customFieldsErrors[fieldKey]?.message;
        const fieldErrorMessage =
          typeof fieldError === "string" ? fieldError : undefined;

        return (
          <FieldContainer key={fieldKey}>
            <Label htmlFor={inputId}>{field.heading}</Label>
            {field.type === CustomFieldType.DROPDOWN ? (
              <Select
                collapseDescriptionSpace
                placeholder="Please select an option..."
                options={[
                  ...((field.options || []).map((option) => ({
                    value: option,
                    label: option,
                  })) as Option[]),
                ]}
                validationStatus={
                  fieldErrorMessage
                    ? {
                        success: false,
                        message: fieldErrorMessage,
                      }
                    : undefined
                }
                value={fieldValue}
                onChange={(selectedOption) =>
                  upsertResponse(fieldKey, selectedOption.value)
                }
                data-tname={inputId}
              />
            ) : (
              <TextArea
                id={inputId}
                rows={3}
                value={fieldValue}
                placeholder={field.placeholder}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  upsertResponse(fieldKey, event.target.value)
                }
                validationStatus={
                  fieldErrorMessage
                    ? {
                        success: false,
                        message: fieldErrorMessage,
                      }
                    : undefined
                }
                data-tname={inputId}
                collapseDescriptionSpace
              />
            )}
          </FieldContainer>
        );
      })}
    </div>
  );
};
